import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Heart, MapPin, CalendarDays, Ticket, ExternalLink, Car, TrainFront, Footprints, X } from "lucide-react";
import { toast } from "sonner";

// Espace parent : les activités que l'enfant a marquées « Je veux y aller ! ».
// Les itinéraires partent de la localisation du PARENT (jamais celle de l'enfant).
export default function ChildWishes() {
  const [wishes, setWishes] = useState([]);
  const [unread, setUnread] = useState(0);
  const load = () => api.get("/kids-wishes").then(({ data }) => { setWishes(data.wishes || []); setUnread(data.unread || 0); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const dir = (addr, mode) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}&travelmode=${mode}`;
    window.open(url, "_blank", "noopener");
  };
  const remove = async (id) => { try { await api.delete(`/kids-wishes/${id}`); load(); } catch (_) { toast.error("Erreur"); } };
  const markSeen = async () => {
    try { await api.post("/kids-wishes/seen"); setUnread(0); setWishes((ws) => ws.map((w) => ({ ...w, status: "seen" }))); }
    catch (_) { toast.error("Erreur"); }
  };

  if (!wishes.length) return null;

  return (
    <section className="mb-10" data-testid="child-wishes">
      <div className="flex items-center gap-2 mb-4">
        <Heart size={20} className="text-gold" />
        <h2 className="font-display text-xl" style={{ color: "var(--text-main)" }}>Les envies de mon enfant</h2>
        {unread > 0 && (
          <span data-testid="wishes-unread-badge"
            className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold text-white"
            style={{ background: "#EF4444" }}>{unread}</span>
        )}
        {unread > 0 && (
          <button data-testid="wishes-mark-seen" onClick={markSeen}
            className="ml-auto text-xs font-medium underline" style={{ color: "var(--accent)" }}>Marquer comme vues</button>
        )}
      </div>
      <div className="space-y-3">
        {wishes.map((w) => {
          const e = w.event || {};
          return (
            <div key={w.wish_id} data-testid={`wish-${w.wish_id}`} className="card-surface p-4"
              style={w.status === "new" ? { boxShadow: "0 0 0 2px var(--accent)" } : undefined}>
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <p className="text-xs flex items-center gap-2" style={{ color: "var(--accent)" }}>
                    {w.child_name} aimerait&nbsp;:
                    {w.status === "new" && (
                      <span data-testid={`wish-new-${w.wish_id}`} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: "#EF4444" }}>Nouveau</span>
                    )}
                  </p>
                  <p className="font-display text-lg" style={{ color: "var(--text-main)" }}>{e.name}</p>
                  <p className="text-sm mt-1 flex items-center gap-1.5" style={{ color: "color-mix(in srgb,var(--text-main) 70%,transparent)" }}><CalendarDays size={13} /> {e.date}{e.end_date ? ` → ${e.end_date}` : ""} {e.time ? `· ${e.time}` : ""}</p>
                  <p className="text-sm flex items-center gap-1.5" style={{ color: "color-mix(in srgb,var(--text-main) 70%,transparent)" }}><MapPin size={13} /> {e.address}, {e.city} · {e.age_min}-{e.age_max} ans</p>
                  <p className="text-sm flex items-center gap-1.5" style={{ color: "color-mix(in srgb,var(--text-main) 70%,transparent)" }}><Ticket size={13} /> {e.is_free ? "Gratuit" : (e.price || "Payant")}</p>
                  {e.parent_summary && <p className="text-sm mt-2" style={{ color: "var(--text-main)" }}>{e.parent_summary}</p>}
                  {e.source && <p className="text-xs mt-1" style={{ color: "color-mix(in srgb,var(--text-main) 55%,transparent)" }}>Source : {e.source}</p>}
                </div>
                <button onClick={() => remove(w.wish_id)} className="text-white/40 hover:text-white shrink-0" aria-label="Retirer"><X size={16} /></button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {e.official_url && (
                  <a href={e.official_url} target="_blank" rel="noopener noreferrer" data-testid={`wish-book-${w.wish_id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium" style={{ background: "var(--accent-gradient)", color: "var(--accent-fg)" }}>
                    <ExternalLink size={15} /> Infos & réservation
                  </a>
                )}
                <button onClick={() => dir(`${e.address}, ${e.city}`, "driving")} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm border" style={{ borderColor: "var(--surface-border)", color: "var(--text-main)" }}><Car size={15} /> Voiture</button>
                <button onClick={() => dir(`${e.address}, ${e.city}`, "transit")} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm border" style={{ borderColor: "var(--surface-border)", color: "var(--text-main)" }}><TrainFront size={15} /> Transports</button>
                <button onClick={() => dir(`${e.address}, ${e.city}`, "walking")} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm border" style={{ borderColor: "var(--surface-border)", color: "var(--text-main)" }}><Footprints size={15} /> À pied</button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
